/**
 * Mensagens que a pessoa mandou com o agente ainda respondendo (AB#86889, task 87271).
 *
 * Como no Claude Code, a mensagem aparece no ponto do trabalho em que entrou: o rastro do turno
 * é cortado ali, e o que o agente fez depois vem abaixo dela. `traceIndex` é o tamanho do rastro
 * no momento do envio. A que foi para a fila (o turno já fechava, ou rodava noutra réplica) não
 * entra no turno: vem depois da resposta, porque roda num turno próprio.
 */
export interface JoinedMessage {
  messageId: string;
  traceIndex: number;
  queued?: boolean;
}

export interface TraceSegment<T> {
  entries: T[];
  /** A mensagem da pessoa que vem logo depois deste trecho do rastro. */
  messageAfter?: string;
}

/** Corta o rastro nos pontos em que a pessoa escreveu; o último trecho é o que segue rodando. */
export function splitTrace<T>(trace: T[], joined: JoinedMessage[]): TraceSegment<T>[] {
  const segments: TraceSegment<T>[] = [];
  let start = 0;
  for (const message of joined) {
    if (message.queued) continue;
    const end = Math.min(Math.max(message.traceIndex, start), trace.length);
    segments.push({ entries: trace.slice(start, end), messageAfter: message.messageId });
    start = end;
  }
  segments.push({ entries: trace.slice(start) });
  return segments;
}

export interface TimelineMessage {
  id: string;
  created_at: string;
}

const justBefore = (iso: string) => new Date(new Date(iso).getTime() - 1).toISOString();

/**
 * Põe um turno no lugar dentro da conversa: os trechos do rastro ("Pensamento") com a mensagem que
 * entrou depois de cada um, a resposta e, por fim, as mensagens que foram para a fila. Serve tanto à
 * resposta que acabou de chegar (ainda fora da lista: o turno vai para o fim) quanto ao histórico
 * reaberto (AB#87296: o turno ocupa o lugar da resposta, e as mensagens dele saem de onde a ordem de
 * gravação as deixou).
 */
export function arrangeTurn<T, M extends TimelineMessage>(
  messages: M[],
  answer: M,
  trace: T[],
  joined: JoinedMessage[],
  thinking: (entries: T[], index: number, at: string) => M,
): M[] {
  const joinedIds = new Set(joined.map((j) => j.messageId));
  const byId = new Map(messages.filter((m) => joinedIds.has(m.id)).map((m) => [m.id, m]));
  const turn: M[] = [];
  splitTrace(trace, joined).forEach((segment, index) => {
    const next = segment.messageAfter ? byId.get(segment.messageAfter) : undefined;
    if (segment.entries.length > 0) {
      turn.push(thinking(segment.entries, index, justBefore(next ? next.created_at : answer.created_at)));
    }
    if (next) turn.push(next);
  });
  turn.push(answer);
  for (const j of joined) {
    const queued = j.queued ? byId.get(j.messageId) : undefined;
    if (queued) turn.push(queued);
  }

  const answerIndex = messages.findIndex((m) => m.id === answer.id);
  const rest = messages.filter((m) => !joinedIds.has(m.id) && m.id !== answer.id);
  if (answerIndex < 0) return [...rest, ...turn];
  const before = messages.slice(0, answerIndex).filter((m) => !joinedIds.has(m.id)).length;
  return [...rest.slice(0, before), ...turn, ...rest.slice(before)];
}

/** O rastro que a API devolve com cada resposta do histórico, ou o aviso de que ele não pôde ser lido. */
export type StoredTurnTrace<T> = { entries: T[]; joined: JoinedMessage[] } | { error: true };

/**
 * O histórico reaberto com o "Pensamento" de volta (AB#87296): cada resposta que tem rastro ganha
 * os trechos dele antes de si, cortados onde a pessoa escreveu; a que tem o rastro ilegível ganha o
 * aviso no lugar. Resposta sem rastro (antiga, ou já vencida) fica como está.
 */
export function arrangeHistory<T, M extends TimelineMessage>(
  messages: M[],
  traceOf: (message: M) => StoredTurnTrace<T> | undefined,
  thinking: (answer: M, entries: T[], index: number, at: string) => M,
  unavailable: (answer: M) => M,
): M[] {
  let arranged = messages;
  for (const answer of messages) {
    const trace = traceOf(answer);
    if (!trace) continue;
    if ('error' in trace) {
      const at = arranged.findIndex((m) => m.id === answer.id);
      arranged = [...arranged.slice(0, at), unavailable(answer), ...arranged.slice(at)];
      continue;
    }
    arranged = arrangeTurn(arranged, answer, trace.entries, trace.joined, (entries, index, at) =>
      thinking(answer, entries, index, at),
    );
  }
  return arranged;
}

/** Último ponto de corte: o texto que chegar depois dele começa uma entrada nova no rastro. */
export function lastBoundary(joined: JoinedMessage[]): number {
  return joined.reduce((max, message) => (message.queued ? max : Math.max(max, message.traceIndex)), 0);
}
